import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = ["new", "quoted", "closed"];
const color = { new: "bg-amber-100 text-amber-700", quoted: "bg-blue-100 text-blue-700", closed: "bg-slate-200 text-slate-600" };

export default function Enquiries() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const navigate = useNavigate();
  const [list, setList] = useState([]);

  const load = useCallback(() => { api.get("/enquiries").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/enquiries/${id}/status`, fd);
    toast.success("Enquiry status updated");
    load();
  };

  const createQuotation = (e) => {
    sessionStorage.setItem("sf_quote_prefill", JSON.stringify({
      client_id: e.client_id || "",
      client_name: e.client_name,
      items: e.items.map((it) => ({ name: it.name, qty: it.qty })),
    }));
    navigate("/portal/invoices");
  };

  const itemsText = (e) => e.items.map((i) => `${i.name} ×${i.qty}`).join(", ");
  const attSrc = (p) => fileUrl(p);

  return (
    <div className="p-8">
      <PageHeader title={isAdmin ? "Quotation Enquiries" : "My Enquiries"} subtitle="Requests" />
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Enquiry #</TableHead>
              {isAdmin && <TableHead>Client</TableHead>}
              <TableHead>Items</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Status</TableHead>
              {isAdmin && <TableHead className="text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((e) => (
              <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                <TableCell className="font-mono text-xs">{e.enquiry_number}</TableCell>
                {isAdmin && <TableCell>{e.client_name}<div className="text-xs text-muted-foreground">{e.client_email}</div></TableCell>}
                <TableCell className="max-w-xs text-sm">
                  {itemsText(e)}
                  {e.attachments?.length > 0 && (
                    <div className="flex gap-1 mt-1.5" data-testid={`enquiry-attachments-${e.id}`}>
                      {e.attachments.map((p, k) => (
                        <a key={k} href={attSrc(p)} target="_blank" rel="noreferrer"><img src={attSrc(p)} alt="attachment" className="w-9 h-9 object-cover rounded-sm border border-border hover:opacity-80" /></a>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                  {e.site_location && <div className="flex items-center gap-1 text-secondary mb-0.5" data-testid={`enquiry-location-${e.id}`}><MapPin size={11} className="text-primary shrink-0" /> {e.site_location}</div>}
                  <div className="truncate">{e.note || "—"}</div>
                </TableCell>
                <TableCell>
                  {isAdmin ? (
                    <Select value={e.status} onValueChange={(v) => changeStatus(e.id, v)}>
                      <SelectTrigger className="w-28 h-8 rounded-sm text-xs" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Badge className={`rounded-sm capitalize ${color[e.status]}`}>{e.status}</Badge>}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => createQuotation(e)} className="rounded-sm bg-primary hover:bg-primary/90 gap-1" data-testid={`create-quote-${e.id}`}>
                      <FileText size={14} /> Quotation
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 6 : 4} className="text-center py-12 text-muted-foreground">{isAdmin ? "No enquiries yet." : "You haven't requested any quotations yet."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
